import { singleton } from 'tsyringe';
import { BaseResourceTaggabilityItem, IQueryExpr } from '@apis/Resources/model';
import { exprBuilder, queryBuilder } from '@root/Services/QueryExpr';
import { postResourcesQuery } from '@apis/Resources';
import { BaseResource, IResourceGridSelection } from '@root/Components/Resources/ResourcesGrid';

@singleton()
export class GcpLabelService {
    async hasAnyLabelable(query: IQueryExpr | undefined): Promise<boolean> {
        try {
            if (!query) {
                return false;
            }

            const { builder: xb } = exprBuilder<BaseResource>();
            const selectionCriteria = !query ? [] : [xb.fromExpr<boolean>(query)];
            const results = await queryBuilder<BaseResource>()
                .where((b) =>
                    b.and(
                        b.model.CloudPlatform!.eq('Gcp'),
                        b.model.Taggability!.eq([BaseResourceTaggabilityItem.DelegatedLabelable, BaseResourceTaggabilityItem.Labelable]),
                        ...selectionCriteria
                    )
                )
                .select((b) => ({ count: b.count() }))
                .execute(postResourcesQuery);

            const count = results?.Results?.[0]?.count ?? 0;
            return count > 0;
        } catch (err) {
            console.error('Failed to check for labelable resources', err);
            return false;
        }
    }

    async hasAnyLabelableBySelection(selection: IResourceGridSelection): Promise<boolean> {
        try {
            let query = selection.getResourceQuery();

            if (!query) {
                const included = selection.getIncluded();
                if (included.length === 0) {
                    return false;
                }

                const resourceQueries = included.map((id) => ({
                    Operation: 'and' as const,
                    Operands: [
                        { Operation: 'eq' as const, Operands: [{ Field: 'Id' }, { Value: id.Id }] },
                        { Operation: 'eq' as const, Operands: [{ Field: 'ResourceType' }, { Value: id.ResourceType }] },
                        { Operation: 'eq' as const, Operands: [{ Field: 'CloudPlatform' }, { Value: id.CloudPlatform }] },
                    ],
                }));

                query = resourceQueries.length === 1 ? resourceQueries[0] : { Operation: 'or', Operands: resourceQueries };
            }

            const labelableWhere: IQueryExpr = {
                Operation: 'and',
                Operands: [
                    { Operation: 'eq', Operands: [{ Field: 'CloudPlatform' }, { Value: 'Gcp' }] },
                    {
                        Operation: 'or',
                        Operands: [
                            { Operation: 'contains', Operands: [{ Field: 'Taggability' }, { Value: BaseResourceTaggabilityItem.Labelable }] },
                            {
                                Operation: 'contains',
                                Operands: [{ Field: 'Taggability' }, { Value: BaseResourceTaggabilityItem.DelegatedLabelable }],
                            },
                        ],
                    },
                ],
            };

            const labelableQuery = await queryBuilder<BaseResource>()
                .select((b) => ({ count: b.count() }))
                .build();

            labelableQuery.Where = {
                Operation: 'and',
                Operands: [query, labelableWhere],
            };

            const results = (await postResourcesQuery(labelableQuery)) as any;
            const labelableCount = results?.Results?.[0]?.count ?? 0;

            const totalCount = selection.getIncluded().length;

            return labelableCount > 0 && labelableCount === totalCount;
        } catch (err) {
            console.error('Failed to check for labelable resources by selection', err);
            return false;
        }
    }
}
